import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Box, SxProps, Typography } from '@mui/material';
import { Acompte } from '@europrocurement/l2d-modules/providers';
import { useOrigine } from '@europrocurement/l2d-modules/modules/ParcoursFormalite/hooks';
import { Uploader, useUploader } from '@europrocurement/flexy-components';

export type ProvisionAddType = {
    onAddProvision: (acompte: Acompte, files?: File[]) => Promise<void>;
    collectionId: number;
    overwriteDefaultProvisionAdd?: Partial<ProvisionAddFormObject>;
    sx?: SxProps;
};

export type EncaissementOption = {
    label: string;
    value: 'CH' | 'ESP' | 'VIR';
};

export type ProvisionAddFormObject = {
    mode: EncaissementOption;
    montant: string;
    date?: Date;
    reference?: string;
    note?: string;
};

const ProvisionAdd: React.FunctionComponent<ProvisionAddType> = function ({
    collectionId,
    onAddProvision,
    overwriteDefaultProvisionAdd,
    sx,
}) {
    const origine = useOrigine();
    const { files } = useUploader();

    const defaultProvisionAdd: Partial<ProvisionAddFormObject> = {
        mode: {
            label: 'Virement bancaire',
            value: 'VIR',
        },
        montant: '0',
        date: new Date(),
        reference: undefined,
        note: undefined,
        ...overwriteDefaultProvisionAdd,
    };

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: defaultProvisionAdd,
    });

    const formStructure: FormStructure[] = React.useMemo(
        () => [
            {
                type: 'header',
                label: 'Ajouter un encaissement',
                name: 'main_informations',
            },
            {
                type: 'select',
                name: 'mode',
                inputlabel: "Mode d'encaissement",
                options: [
                    {
                        label: 'Chèque',
                        value: 'CH',
                    },
                    {
                        label: 'Espèces',
                        value: 'ESP',
                    },
                    {
                        label: 'Virement bancaire',
                        value: 'VIR',
                    },
                ],
                placeholder: "Veuillez sélectionner un mode d'encaissement",
                rules: {
                    required: "Vous devez sélectionner un mode d'encaissement",
                },
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'number',
                inputProps: {
                    step: 1,
                    min: 0,
                },
                name: 'montant',
                inputlabel: 'Montant (€)',
                placeholder: '0.00',
                rules: {
                    required: 'Vous devez renseigner un montant',
                },
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'date',
                name: 'date',
                inputlabel: 'Date',
                disableFuture: true,
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'text',
                name: 'reference',
                inputlabel: 'Référence',
                placeholder: 'REF001',
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'text',
                name: 'note',
                inputlabel: 'Note',
                placeholder: 'Note de la transaction',
                xs: 12,
                sm: 12,
                md: 12,
                lg: 12,
            },
            {
                type: 'customItem',
                name: 'attachmentsUploader',
                renderField: () => (
                    <Box sx={{ display: 'grid', gap: '4px' }}>
                        <Typography variant="l2dlabel">Documents</Typography>
                        <Uploader
                            label="Documents"
                            sx={{ margin: 0 }}
                            fileUlpoadOverides={{ height: '160px' }}
                        />
                    </Box>
                ),
            },
        ],
        [],
    );

    const valueOrUndefined = (fieldToTest?: string | null): string | undefined =>
        fieldToTest !== undefined && fieldToTest !== null && fieldToTest !== ''
            ? (fieldToTest as unknown as string)
            : undefined;

    const onSubmit = async () => {
        onAddProvision(
            {
                mode: formContext.getValues().mode?.value as 'CH' | 'ESP' | 'VIR',
                montant: Number(formContext.getValues().montant),
                reference: valueOrUndefined(formContext.getValues().reference as unknown as string),
                origine,
                detailsReglements: [
                    {
                        idCollection: collectionId as unknown as number,
                        ttc: formContext.getValues().montant as unknown as number,
                    },
                ],
                note: valueOrUndefined(formContext.getValues().note as unknown as string),
                dreception: formContext.getValues().date,
                usercreation: 42, // TODO LLC
            },
            files,
        );
    };

    const formProps = {
        formContext,
        onSubmit,
        formObject: {},
    };

    return (
        <Box sx={{ ...sx }}>
            <FlexyForm
                formStructure={formStructure}
                {...formProps}
            />
        </Box>
    );
};

export default ProvisionAdd;
